"use client";

import { useEffect, useRef, useState } from "react";
import Image, { type ImageProps } from "next/image";
import { Link, type LinkProps, Section } from "@marketing-sites/ui/primitives";
import { useIsMobile } from "@marketing-sites/hooks/use-mobile";
import { cn } from "@marketing-sites/utils/cn";

export interface FeatureShowcaseItem {
  id: string;
  label: string;
  description: string;
  image: Required<Pick<ImageProps, "src" | "alt">>;
  link: Pick<LinkProps, "href"> & { analyticsId: string };
}

interface FeatureShowcaseProps {
  items: FeatureShowcaseItem[];
}

const PANEL_IMAGE_SIZES = "(min-width: 1920px) 600px, 33vw";
const THUMBNAIL_IMAGE_SIZES = "240px";
const IMAGE_FRAME = "relative aspect-square overflow-hidden";

function StackedImages({
  items,
  activeId,
  sizes,
}: {
  items: FeatureShowcaseItem[];
  activeId: string;
  sizes: string;
}) {
  // Every image stays mounted and crossfades by opacity, so switching items never waits on a fetch.
  return items.map((item) => {
    const isActive = item.id === activeId;
    return (
      <Image
        key={item.id}
        src={item.image.src}
        alt={isActive ? item.image.alt : ""}
        fill
        sizes={sizes}
        className={cn(
          "object-cover transition-opacity duration-500 ease-in-out",
          isActive ? "opacity-100" : "opacity-0",
        )}
      />
    );
  });
}

export default function FeatureShowcase({ items }: FeatureShowcaseProps) {
  const isMobile = useIsMobile();
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const activeItem = items.find((item) => item.id === activeId) ?? items[0];

  // Scrolling a row through the middle band of the viewport makes it active, as on the legacy /helpdesk showcase.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = rowRefs.current.indexOf(entry.target as HTMLLIElement);
          const item = items[index];
          if (item) setActiveId(item.id);
        });
      },
      {
        rootMargin: isMobile ? "-25% 0px -65% 0px" : "-45% 0px -45% 0px",
        threshold: 0.5,
      },
    );
    rowRefs.current.forEach((row) => row && observer.observe(row));
    return () => observer.disconnect();
  }, [isMobile, items]);

  if (!activeItem) return null;

  return (
    <Section
      id="features-showcase"
      data-analytics-region="Features Showcase"
      data-analytics-id="sec_helpdesk_feature_showcase"
      gridCols="10"
      dotDecorators={false}
      className="md:pb-42 py-16 md:pt-20"
    >
      <div className="col-span-full hidden lg:col-span-4 lg:block">
        <div
          data-analytics-region="Side Panel"
          data-analytics-id="sec_helpdesk_feature_showcase_side_panel"
          className="lg:top-25 lg:sticky"
        >
          <div className={cn(IMAGE_FRAME, "w-full")}>
            <StackedImages
              items={items}
              activeId={activeItem.id}
              sizes={PANEL_IMAGE_SIZES}
            />
          </div>
          <p className="text-body text-current/70 mt-4 font-sans">
            {activeItem.description}
          </p>
        </div>
      </div>

      <div className="col-span-full lg:col-span-5 lg:col-start-6">
        <ul className="text-heading-3 flex flex-col gap-3">
          {items.map((item, index) => {
            const isActive = item.id === activeItem.id;
            return (
              <li
                key={item.id}
                ref={(row) => {
                  rowRefs.current[index] = row;
                }}
                onMouseEnter={() => setActiveId(item.id)}
                onFocus={() => setActiveId(item.id)}
                className="border-clay border-b pb-3"
              >
                <Link
                  // eslint-disable-next-line analytics/id-must-be-static -- each item in the items prop carries its own hardcoded analyticsId
                  analyticsId={item.link.analyticsId}
                  analyticsLabel={item.label}
                  href={item.link.href}
                  className={cn(
                    "flex items-start justify-between gap-4 transition-colors duration-300",
                    isActive ? "text-current" : "text-current/40",
                  )}
                >
                  <h3>{item.label}</h3>
                  <span
                    aria-hidden
                    className={cn(
                      "transition-opacity duration-300",
                      isActive ? "opacity-100" : "opacity-0",
                    )}
                  >
                    ↗
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="sticky bottom-3 z-10 mt-9 lg:hidden">
          <div className={cn(IMAGE_FRAME, "bg-off-white/95 w-60")}>
            <StackedImages
              items={items}
              activeId={activeItem.id}
              sizes={THUMBNAIL_IMAGE_SIZES}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}
